import React, { useEffect, useState } from "react";
import { getCautionFeesSummary } from "@services/krent.service";

interface SummaryCardProps {
  label: string;
  amount: string;
  badge: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, amount, badge }) => (
  <div
    className="col"
    style={{
      flex: "1 1 0",
      minWidth: 220,
    }}
  >
    <div
      style={{
        background: "#f7f8f8",
        border: "1px solid #e8e9ea",
        borderRadius: 16,
        padding: "28px 28px 24px",
        height: "100%",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <p
        style={{
          fontSize: "0.9rem",
          fontWeight: 600,
          color: "#2d2d2d",
          marginBottom: 8,
        }}
      >
        {label}
      </p>

      {/* Amount */}
      <h3
        style={{
          fontSize: "clamp(1.5rem, 2.5vw, 2rem)",
          fontWeight: 700,
          color: "#1a1a1a",
          marginBottom: 20,
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
        }}
      >
        {amount}
      </h3>

      {/* Badge */}
      <span
        style={{
          display: "inline-block",
          background: "#3bab8c",
          color: "#fff",
          fontSize: "0.8rem",
          fontWeight: 600,
          padding: "6px 16px",
          borderRadius: 999,
          letterSpacing: "0.01em",
        }}
      >
        {badge}
      </span>
    </div>
  </div>
);

// ── Summary data ───────────────────────────────────────────────────────────
const CautionFeeSummary: React.FC = () => {
  const [summaryData, setSummaryData] = useState<SummaryCardProps[]>([
    {
      label: "Total caution fee held",
      amount: "NGN 0.00",
      badge: "0 bookings",
    },
    {
      label: "Eligible for refund",
      amount: "NGN 0.00",
      badge: "0 bookings ready",
    },
    {
      label: "Total paid out",
      amount: "NGN 0.00",
      badge: "All time",
    },
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response: any = await getCautionFeesSummary();
        const data = response?.data || response;

        if (data) {
          const formatNaira = (amount: number) =>
            `NGN ${amount.toLocaleString("en-NG")}`;

          setSummaryData([
            {
              label: "Total caution fee held",
              amount: formatNaira(data.totalCautionFeeHeld?.amount || 0),
              badge:
                data.totalCautionFeeHeld?.label ||
                `${data.totalCautionFeeHeld?.bookingCount || 0} bookings`,
            },
            {
              label: "Eligible for refund",
              amount: formatNaira(data.eligibleForRefund?.amount || 0),
              badge:
                data.eligibleForRefund?.label ||
                `${data.eligibleForRefund?.bookingCount || 0} bookings ready`,
            },
            {
              label: "Total paid out",
              amount: formatNaira(data.totalPaidOut?.amount || 0),
              badge: data.totalPaidOut?.label || "All time",
            },
          ]);
        }
      } catch (error) {
        console.error("Error fetching caution fees summary:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          gap: 20,
          flexWrap: "wrap",
          padding: "16px 0",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="col"
            style={{
              flex: "1 1 0",
              minWidth: 220,
            }}
          >
            <div
              style={{
                background: "#f7f8f8",
                border: "1px solid #e8e9ea",
                borderRadius: 16,
                padding: "28px 28px 24px",
                height: "100%",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              <p
                style={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#2d2d2d",
                  marginBottom: 8,
                }}
              >
                Loading...
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
      <div
        style={{
          display: "flex",
          gap: 20,
          flexWrap: "wrap",
          padding: "16px 0",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {summaryData.map((card) => (
          <SummaryCard key={card.label} {...card} />
        ))}
      </div>
    </>
  );
};

export default CautionFeeSummary;
